import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalDropdownField } from '../../../components/GlobalComponents/GlobalDropdownField';

interface ExamTypeOption {
  label: string;
  value: string;
  title: string;
  name: string;
  status: string;
}

interface ExamTypeResponse {
  status: string;
  message?: string;
  data: Array<{
    id: string;
    title: string;
    name: string;
    status: string;
  }>;
}

const ExamCalendarSettings: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [examTypeOptions, setExamTypeOptions] = useState<ExamTypeOption[]>([]);
  const [selectedExamTypeId, setSelectedExamTypeId] = useState<string | null>(null);

  useEffect(() => {
    fetchExamTypeSettings();
  }, []);

  const fetchExamTypeSettings = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await apiClient.get<ExamTypeResponse>('/exam-types');
      const responseData = response.data;

      if (responseData.status === 'success' && responseData.data) {
        const options: ExamTypeOption[] = responseData.data.map((item) => ({
          label: `${item.title} (${item.name})${item.status === 'active' ? ' - Current' : ''}`,
          value: item.id,
          title: item.title,
          name: item.name,
          status: item.status,
        }));

        setExamTypeOptions(options);

        const activeOption = options.find((opt) => opt.status === 'active');
        if (activeOption) {
          setSelectedExamTypeId(activeOption.value);
        } else if (options.length > 0 && !selectedExamTypeId) {
          setSelectedExamTypeId(options[0].value);
        }
      } else {
        throw new Error(responseData?.message || 'Invalid response format');
      }
    } catch (err: any) {
      console.error('Failed to fetch exam type settings:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to fetch exam type settings',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveExamTypeSettings = async (): Promise<void> => {
    if (!selectedExamTypeId) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select an exam type',
        life: 4000,
      });
      return;
    }

    setSaving(true);
    try {
      const response = await apiClient.post(
        `/exam-types/${selectedExamTypeId}/set-active`,
        {}
      );

      if (response.data.status === 'success') {
        setExamTypeOptions((prev) =>
          prev.map((option) => ({
            ...option,
            status: option.value === selectedExamTypeId ? 'active' : 'inactive',
            label: `${option.title} (${option.name})${option.value === selectedExamTypeId ? ' - Current' : ''}`,
          }))
        );

        const selectedOption = examTypeOptions.find(
          (opt) => opt.value === selectedExamTypeId
        );

        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: `Exam type updated successfully${selectedOption ? ` - ${selectedOption.title}` : ''}`,
          life: 4000,
        });
      } else {
        throw new Error(response.data.message || 'Failed to update exam type settings');
      }
    } catch (err: any) {
      console.error('Failed to save exam type settings:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to update exam type settings',
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    fetchExamTypeSettings();
  };

  if (loading) {
    return (
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '60px 24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.12)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }}
        ></div>
        <p style={{ color: '#64748b', fontSize: '14px' }}>Loading exam type settings...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.12)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: '2px solid #f3f4f6',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="pi pi-book" style={{ fontSize: '18px', color: 'white' }}></i>
            </div>
            <div>
              <h2
                style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1e40af',
                  margin: 0,
                }}
              >
                Exam Calendar
              </h2>
              <p
                style={{
                  fontSize: '13px',
                  color: '#64748b',
                  margin: '2px 0 0 0',
                }}
              >
                Select the active exam type for the current period
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <GlobalButton
              label=""
              onClick={handleRefresh}
              disabled={loading}
              icon="pi pi-refresh"
              variant="outline"
              size="small"
            />

            <GlobalButton
              label={saving ? 'Updating...' : 'Update Settings'}
              onClick={handleSaveExamTypeSettings}
              disabled={saving || examTypeOptions.length === 0}
              loading={saving}
              icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              variant="success"
              size="small"
            />
          </div>
        </div>

        {/* Content */}
        <div style={{ maxWidth: '500px' }}>
          <GlobalDropdownField
            label="Select Current Exam Type"
            value={selectedExamTypeId}
            onChange={(value) => setSelectedExamTypeId(value)}
            options={examTypeOptions}
            placeholder={examTypeOptions.length === 0 ? 'No exam types available' : 'Select an exam type'}
            disabled={saving || examTypeOptions.length === 0}
            fullWidth
            icon="pi pi-book"
          />

          <p
            style={{
              fontSize: '12px',
              color: '#6b7280',
              marginTop: '8px',
              lineHeight: 1.5,
            }}
          >
            {examTypeOptions.length > 0 ? (
              <>
                {examTypeOptions.length} option{examTypeOptions.length !== 1 ? 's' : ''} available.
                Select the exam type that should be active for the current period.
              </>
            ) : (
              'No exam types available. Please create an exam type first.'
            )}
          </p>
        </div>
      </div>
    </>
  );
};

export default ExamCalendarSettings;